import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { Box, Divider } from "@mui/material";
import { useState } from "react";
import { Currency } from "../Purchase.types.ts";

export interface AiFeatureTermsDialogProps {
    currency: Currency;
    onClose: () => void;
    onAccept: () => void;
}

function AiFeatureTermsDialog(props: AiFeatureTermsDialogProps) {
    const [termsAccepted, setTermsAccepted] = useState(false);

    const isDkk = props.currency === Currency.DKK;
    const currencySuffix = isDkk ? "DKK" : "EUR";
    const dataExtractionPrice = isDkk ? 150 : 20;
    const qualityScanPrice = isDkk ? 300 : 40;
    const welcomeCredit = isDkk ? 75 : 10;

    const bulletPoints = [
        "Data is processed by a European AI provider",
        <>
            Data is <b>never</b> used for AI training
        </>,
        "Data is not stored by our AI provider",
        "AI features can be disabled at any time",
        "Always verify AI-generated outputs before use",
    ];

    return (
        <DotLegalDialog buttonOkText={"Accept"} open onDialogClose={props.onClose} onOkClick={props.onAccept} size={"sm"} okButtonDisabled={!termsAccepted}>
            <DotLegalHeader headerStyle={"xxxl"} marginTop={-2} marginBottom={1}>
                Activate AI Features
            </DotLegalHeader>
            <DotLegalHeader headerStyle={"small"} fontWeight={400} marginBottom={2}>
                AI Features has a usage-based cost depending on the amount of content processed. Set a monthly spending limit for your organisation's AI
                consumption. If the limit is reached, AI features will be paused until the next month begins or the limit is increased.{" "}
                <DotLegalLink linkColor="primary" to="https://www.dotlegal.com/en/product/ai-features/">
                    Learn more
                </DotLegalLink>
                .
            </DotLegalHeader>

            <Box sx={(theme) => ({ mb: 2, borderRadius: "12px", backgroundColor: theme.palette.primary.light, padding: 2 })}>
                <Box
                    sx={(theme) => ({
                        fontSize: theme.typography.pxToRem(12),
                        fontWeight: 500,
                        color: theme.palette.primary.dark,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        mb: 0.75,
                    })}
                >
                    Key points
                </Box>

                <Box
                    component={"ul"}
                    sx={(theme) => ({ px: "20px", margin: 0, color: theme.palette.primary.dark, "& li": { fontSize: theme.typography.pxToRem(13) } })}
                >
                    {bulletPoints.map((bulletPoint, index) => {
                        return (
                            <Box key={index} component={"li"}>
                                {bulletPoint}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Box
                    sx={(theme) => ({
                        fontSize: theme.typography.pxToRem(12),
                        fontWeight: 500,
                        color: theme.palette.primary.dark,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        mb: 0.75,
                    })}
                >
                    Pricing examples
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
                    <Box
                        sx={(theme) => ({
                            borderRadius: "12px",
                            backgroundColor: theme.palette.grey[200],
                            border: `0.5px solid ${theme.palette.divider}`,
                            padding: "14px 16px",
                        })}
                    >
                        <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(20), fontWeight: 500, color: theme.palette.primary.dark, mb: 0.5 })}>
                            {dataExtractionPrice} {currencySuffix}
                        </Box>
                        <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(12), color: theme.palette.primary.dark })}>
                            Extract data from 1,000 contracts with Contract Data Extraction
                        </Box>
                    </Box>
                    <Box
                        sx={(theme) => ({
                            borderRadius: "12px",
                            backgroundColor: theme.palette.grey[200],
                            border: `0.5px solid ${theme.palette.divider}`,
                            padding: "14px 16px",
                        })}
                    >
                        <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(20), fontWeight: 500, color: theme.palette.primary.dark, mb: 0.5 })}>
                            {qualityScanPrice} {currencySuffix}
                        </Box>
                        <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(12), color: theme.palette.primary.dark })}>
                            Run 1,000 quality scans with Document Quality Assurance
                        </Box>
                    </Box>
                </Box>

                <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(12), fontStyle: "italic", color: theme.palette.primary.dark })}>
                    Examples are indicative. Actual costs depend on document size and content volume.
                </Box>
            </Box>

            <Box
                sx={{
                    mb: 2,
                    borderRadius: "12px",
                    padding: "14px 12px",
                    backgroundColor: "#ECFDF5",
                    border: "0.5px solid #C0DD97",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Box component="span" sx={{ fontSize: "18px", flexShrink: 0 }}>
                    🎁
                </Box>
                <Box sx={{ fontSize: "13px", color: "#047857" }}>
                    <b>One-time offer:</b> Your first {welcomeCredit} {currencySuffix} is on us. Activate AI Features to get started.
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <DotLegalHeader headerStyle={"small"} fontWeight={400} marginBottom={1}>
                To activate AI Features, you need to accept the AI Terms on behalf of your organisation. Read the terms{" "}
                <DotLegalLink linkColor="primary" to="https://www.dotlegal.com/en/ai-terms">
                    here
                </DotLegalLink>
                .
            </DotLegalHeader>

            <Box sx={{ mb: -2 }}>
                <DotLegalCheckbox
                    checked={termsAccepted}
                    onChange={() => setTermsAccepted(!termsAccepted)}
                    label={
                        <>
                            I accept the{" "}
                            <DotLegalLink linkColor="primary" to="https://www.dotlegal.com/en/ai-terms">
                                AI Terms
                            </DotLegalLink>{" "}
                            on behalf of my organisation.
                        </>
                    }
                />
            </Box>
        </DotLegalDialog>
    );
}

export default AiFeatureTermsDialog;
